import React from 'react';
import { Link } from 'react-router-dom';

// --- STYLE OBJECTS FOR INLINE STYLING ---
// This approach removes the dependency on external Tailwind CSS configuration.
const styles = {
  pageContainer: {
    backgroundColor: '#f9fafb',
    fontFamily: 'sans-serif',
    color: '#1f2937',
    padding: '4rem 1rem',
  },
  mainContainer: {
    maxWidth: '80rem',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '4rem',
  },
  h1: {
    fontSize: '3rem',
    fontWeight: 'bold',
    letterSpacing: '-0.025em',
  },
  pHeader: {
    marginTop: '1rem',
    fontSize: '1.125rem',
    color: '#4b5563',
    maxWidth: '42rem',
    margin: '1rem auto 0',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3rem',
  },
  sidebar: {
    width: '100%',
  },
  mainContent: {
    width: '100%',
  },
  section: {
    backgroundColor: '#ffffff',
    padding: '2rem',
    borderRadius: '0.75rem',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
  },
  h2: {
    fontSize: '1.5rem',
    fontWeight: '600',
    paddingBottom: '0.5rem',
    borderBottom: '1px solid #e5e7eb',
    marginBottom: '1rem',
  },
  h3: {
    fontSize: '1.25rem',
    fontWeight: '600',
    marginBottom: '0.5rem',
  },
  p: {
    marginBottom: '1rem',
    lineHeight: '1.6',
  },
  code: {
    backgroundColor: '#f3f4f6',
    padding: '0.2rem 0.4rem',
    borderRadius: '0.25rem',
    fontFamily: 'monospace',
    fontSize: '0.9em',
  },
  pre: {
    backgroundColor: '#1f2937',
    color: '#d1d5db',
    padding: '1rem',
    borderRadius: '0.5rem',
    overflowX: 'auto',
    fontSize: '0.875rem',
  },
  infoBox: {
    backgroundColor: '#eff6ff',
    borderLeft: '4px solid #3b82f6',
    padding: '1rem',
    borderRadius: '0 0.5rem 0.5rem 0',
  },
  infoBoxTitle: {
    fontWeight: '600',
    color: '#1e40af',
  },
  ul: {
    listStyle: 'disc',
    paddingLeft: '1.5rem',
    marginTop: '0.5rem',
  },
  link: {
    color: '#2563eb',
    fontWeight: '500',
    textDecoration: 'none',
  },
  table: {
    minWidth: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
    overflow: 'hidden',
  },
  thead: {
    backgroundColor: '#f9fafb',
  },
  th: {
    padding: '0.75rem 1.5rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  tbody: {
    backgroundColor: '#ffffff',
  },
  td: {
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e5e7eb',
    verticalAlign: 'top',
  },
  required: {
    color: '#dc2626',
    fontWeight: 'bold',
    marginLeft: '0.5rem',
  }
};

// Add responsive styles for larger screens
const mediaQuery = '@media (min-width: 1024px)';
styles.contentWrapper[mediaQuery] = { flexDirection: 'row' };
// --- THIS IS THE LINE THAT WAS FIXED ---
styles.sidebar[mediaQuery] = { width: '25%' }; // Removed sticky positioning
styles.mainContent[mediaQuery] = { width: '75%' };


export default function ApiDocumentationPage() {
  return (
    <div style={styles.pageContainer}>
      {/* Injecting a style tag for media queries */}
      <style>{`
        @media (min-width: 1024px) {
          .content-wrapper { flex-direction: row; }
          /* --- THIS IS THE LINE THAT WAS FIXED --- */
          .sidebar { width: 25%; } /* Removed sticky positioning */
          .main-content { width: 75%; }
        }
      `}</style>

      <div style={styles.mainContainer}>
        <header style={styles.header}>
          <h1 style={styles.h1}>Developer API</h1>
          <p style={styles.pHeader}>
            Integrate Greatly Social with your favorite tools using our Zapier integration and public API.
          </p>
        </header>

        <div className="content-wrapper" style={styles.contentWrapper}>
          {/* --- Sidebar Navigation (Simplified for this context) --- */}
          <aside className="sidebar" style={styles.sidebar}>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <a href="#authentication" style={styles.link}>Authentication</a>
              <a href="#trigger-new-post" style={styles.link}>New Post (Trigger)</a>
              <a href="#action-create-post" style={styles.link}>Create Post (Action)</a>
            </nav>
          </aside>

          {/* --- Main Content --- */}
          <main className="main-content" style={{...styles.mainContent, display: 'flex', flexDirection: 'column', gap: '2rem'}}>
            
            <div id="authentication" style={styles.section}>
              <h2 style={styles.h2}>Authentication</h2>
              <p style={styles.p}>The Greatly Social API uses API Key authentication. To authenticate your requests, you must include a valid API key in the <code style={styles.code}>X-API-Key</code> HTTP header.</p>
              <div style={styles.infoBox}>
                <h4 style={styles.infoBoxTitle}>Requirements</h4>
                <ul style={styles.ul}>
                  <li>API access is exclusive to users on the <strong style={{fontWeight: '600'}}>Agency Plan</strong>.</li>
                  <li>Generate and manage your API keys from your <Link to="/dashboard/settings/api-keys" style={styles.link}>API Keys settings page</Link>.</li>
                </ul>
              </div>
              <h4 style={{...styles.h3, marginTop: '1.5rem'}}>Example Header:</h4>
              <pre style={styles.pre}><code>X-API-Key: gsl_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx</code></pre>
            </div>

            <div id="trigger-new-post" style={styles.section}>
              <h2 style={styles.h2}>New Post (Trigger)</h2>
              <p style={{...styles.p, fontFamily: 'monospace'}}><span style={{backgroundColor: '#dcfce7', color: '#166534', fontWeight: 'bold', padding: '0.25rem 0.5rem', borderRadius: '9999px'}}>GET</span> /api/zapier/posts</p>
              <p style={styles.p}>This endpoint is used by Zapier to poll for new posts created in your Greatly Social account. It returns an array of the most recent post objects, sorted in descending order by creation date.</p>
              <h4 style={{...styles.h3, marginTop: '1.5rem'}}>Successful Response (200 OK):</h4>
              <pre style={styles.pre}><code>
{`[
  {
    "id": 17,
    "caption": "Hello from Zapier!",
    "media_url": "https://files.greatlysocial.com/...",
    "created_at": "2025-08-28T16:15:00.000Z",
    "tiktok_id": "_000boAU7aBvk1yeXFeFRDkUAUw4OoT4lINY",
    "privacy_level": "public"
  }
]`}
              </code></pre>
            </div>

            <div id="action-create-post" style={styles.section}>
              <h2 style={styles.h2}>Create Post (Action)</h2>
              <p style={{...styles.p, fontFamily: 'monospace'}}><span style={{backgroundColor: '#dbeafe', color: '#1e40af', fontWeight: 'bold', padding: '0.25rem 0.5rem', borderRadius: '9999px'}}>POST</span> /api/zapier/posts</p>
              <p style={styles.p}>Creates a new post in your Greatly Social account and immediately publishes it to the specified TikTok account. The request body must be a JSON object.</p>
              <h4 style={{...styles.h3, marginTop: '1.5rem'}}>Request Body Parameters:</h4>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead style={styles.thead}>
                    <tr>
                      <th style={styles.th}>Parameter</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Description</th>
                    </tr>
                  </thead>
                  <tbody style={styles.tbody}>
                    <tr>
                      <td style={styles.td}><code style={styles.code}>tiktok_id</code></td>
                      <td style={styles.td}>string<span style={styles.required}>*required</span></td>
                      <td style={styles.td}>The ID of the TikTok account to post to.</td>
                    </tr>
                    <tr>
                      <td style={styles.td}><code style={styles.code}>media_url</code></td>
                      <td style={styles.td}>string<span style={styles.required}>*required</span></td>
                      <td style={styles.td}>A public URL to the video file to be published.</td>
                    </tr>
                    <tr>
                      <td style={styles.td}><code style={styles.code}>caption</code></td>
                      <td style={styles.td}>string</td>
                      <td style={styles.td}>The caption for the post.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <h4 style={{...styles.h3, marginTop: '1.5rem'}}>Successful Response (201 Created):</h4>
              <pre style={styles.pre}><code>
{`{
  "id": 18,
  "tiktok_id": "_000boAU7aBvk1yeXFeFRDkUAUw4OoT4lINY",
  "tiktok_post_id": "v_pub_url~v2-1.7543668942548994061",
  "caption": "Hello",
  "media_url": "https://files.greatlysocial.com/...",
  "message": "Post successfully published to TikTok via Greatly Social."
}`}
              </code></pre>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
